import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';

import updateUser from '@/shared/api/auth/updateUser';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import WaveUINotificationBar from '@/shared/components/utilities/WaveUINotificationBar';
import regexpPatterns from '@/shared/constants/regexpPatterns';
import useAuth from '@/shared/contexts/auth/hooks/useAuth';
import User from '@/shared/types/user';

type Form = Pick<
  User,
  'first_name' | 'last_name' | 'phone_number' | 'email_address'
>;

type FormErrors = Partial<User>;

const ContactDetailsForm: FC = () => {
  const { user, setUser } = useAuth();
  const { t } = useTranslation(['userSettings', 'common']);

  const [apiErrorMessage, setApiErrorMessage] = useState('');
  const [isSuccessNotification, setIsSuccessNotification] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<Form>({
    first_name: '',
    last_name: '',
    email_address: '',
    phone_number: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({
    first_name: '',
    last_name: '',
    phone_number: '',
  });

  useEffect(() => {
    if (user) {
      setForm({
        first_name: user.first_name,
        last_name: user.last_name,
        email_address: user.email_address,
        phone_number: user.phone_number,
      });
    }
  }, [user]);

  const validateContactDetails = () => {
    const errorMessages: FormErrors = {};

    !form.first_name
      ? (errorMessages.first_name = t('userSettings:first_name_required'))
      : (errorMessages.first_name = '');

    !form.last_name
      ? (errorMessages.last_name = t('userSettings:last_name_required'))
      : (errorMessages.last_name = '');

    if (!form.phone_number) {
      errorMessages.phone_number = t('userSettings:phone_number_required');
    } else if (!regexpPatterns.phoneNumberValidator.test(form.phone_number)) {
      errorMessages.phone_number = t('userSettings:phone_number_invalid');
    } else {
      errorMessages.phone_number = '';
    }

    setFormErrors((prevState) => ({
      ...prevState,
      ...errorMessages,
    }));

    return !Object.values(errorMessages).some((error) => !!error);
  };

  const handleUpdateContactDetails = async () => {
    if (loading) return;

    setLoading(true);

    const response = await updateUser({
      email: form.email_address,
      data: {
        first_name: form.first_name,
        last_name: form.last_name,
        phone_number: form.phone_number,
      },
    });

    if (response.success) {
      setLoading(false);
      setUser?.(response.data[0]);
      setIsSuccessNotification(true);
    } else {
      setLoading(false);
      setApiErrorMessage(response.msg);
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {apiErrorMessage && (
          <Alert severity='error' onClose={() => setApiErrorMessage('')}>
            {apiErrorMessage}
          </Alert>
        )}

        <Typography variant='h6'>{t('userSettings:contactDetails')}</Typography>

        <WaveUIForm_V2
          fields={[
            {
              field_type: FieldType.Text,
              field_props: {
                name: 'first_name',
                label: t('userSettings:first_name'),
                placeholder: t('userSettings:first_name_placeholder'),
                value: form?.first_name,
                error: !!formErrors.first_name,
                helperText: formErrors.first_name,
              },
            },
            {
              field_type: FieldType.Text,
              field_props: {
                name: 'last_name',
                label: t('userSettings:last_name'),
                placeholder: t('userSettings:last_name_placeholder'),
                value: form?.last_name,
                error: !!formErrors.last_name,
                helperText: formErrors.last_name,
              },
            },
            {
              field_type: FieldType.Text,
              field_props: {
                name: 'email_address',
                label: t('userSettings:email_address'),
                placeholder: t('userSettings:email_address_placeholder'),
                value: form?.email_address,
                disabled: true,
              },
            },
            {
              field_type: FieldType.Text,
              field_props: {
                name: 'phone_number',
                label: t('userSettings:phone_number'),
                placeholder: t('userSettings:phone_number_placeholder'),
                value: form?.phone_number,
                error: !!formErrors.phone_number,
                helperText: formErrors.phone_number,
              },
            },
          ]}
          getFieldsValues={(changedValue) => {
            setForm((prevState) => ({
              ...prevState,
              ...changedValue,
            }));
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant='contained'
            color='primary'
            onClick={async () => {
              const isFormValid = validateContactDetails();

              if (isFormValid) {
                await handleUpdateContactDetails();
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {loading && <CircularProgress color='inherit' size={16} />}
            {t('common:save')}
          </Button>
        </Box>
      </Paper>

      <WaveUINotificationBar
        message={t('userSettings:contactDetailsWereSuccessfullyUpdated')}
        severity='success'
        timeout={2500}
        open={isSuccessNotification}
        close={() => setIsSuccessNotification(false)}
      />
    </>
  );
};

export default ContactDetailsForm;
